import { toLocalISODate, parseLocalDate } from '../utils/dateUtils';
import { confirmDialog, reportLoadError, notify } from '../utils/notify';
import React, { useState, useEffect, useContext } from 'react';
import { benefitsAPI, employeeAPI } from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Modal from '../components/Modal';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import { formatCurrency } from '../utils/formatCurrency';
import { CALCULATION_TYPE_LABELS, labelOf } from '../utils/labels';
import './Benefits.css';

const Benefits = () => {
  const { user } = useContext(AuthContext);
  const { t, locale } = useLanguage();
  // An amount or a percentage without a currency: a type's default is paid
  // in each employee's own currency
  const amountText = (value) => (parseFloat(value) || 0).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const [activeTab, setActiveTab] = useState('types');
  const [benefitTypes, setBenefitTypes] = useState([]);
  const [employeeBenefits, setEmployeeBenefits] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [editingBenefit, setEditingBenefit] = useState(null);
  
  const [typeForm, setTypeForm] = useState({
    name: '',
    code: '',
    description: '',
    calculation_type: 'fixed',
    default_amount: '',
    is_taxable: false,
    is_active: true
  });

  const [assignForm, setAssignForm] = useState({
    employee_id: '',
    benefit_type_id: '',
    amount: '',
    percentage: '',
    start_date: toLocalISODate(new Date()),
    end_date: '',
    notes: ''
  });

  const [editBenefitForm, setEditBenefitForm] = useState({
    amount: '',
    percentage: '',
    end_date: '',
    status: 'active',
    notes: ''
  });

  const [filters, setFilters] = useState({
    employee_id: '',
    status: 'active'
  });

  useEffect(() => {
    loadBenefitTypes();
    loadEmployees();
    if (activeTab === 'assignments') {
      loadEmployeeBenefits();
    }
    // eslint-disable-next-line
  }, [activeTab]);

  const loadBenefitTypes = async () => {
    try {
      setLoading(true);
      const response = await benefitsAPI.getTypes({ is_active: true });
      setBenefitTypes(response.data.benefit_types);
    } catch (error) {
      console.error('Error loading benefit types:', error);
      reportLoadError(error, t('Error loading benefit types'), { main: activeTab === 'types' });
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await employeeAPI.getAll();
      setEmployees(response.data.employees.filter(e => e.status === 'active'));
    } catch (error) {
      console.error('Error loading employees:', error);
      reportLoadError(error, t('Error loading employees'));
    }
  };

  const loadEmployeeBenefits = async () => {
    try {
      setLoading(true);
      const response = await benefitsAPI.getEmployeeBenefits(filters);
      setEmployeeBenefits(response.data.benefits);
    } catch (error) {
      console.error('Error loading employee benefits:', error);
      reportLoadError(error, t('Error loading employee benefits'), { main: true });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateType = async (e) => {
    e.preventDefault();
    try {
      await benefitsAPI.createType(typeForm);
      notify(t('Benefit type created!'), 'success');
      setShowTypeModal(false);
      resetTypeForm();
      loadBenefitTypes();
    } catch (error) {
      notify(error.response?.data?.message || t('Error creating benefit type'), 'error');
    }
  };

  const handleUpdateType = async (e) => {
    e.preventDefault();
    try {
      await benefitsAPI.updateType(editingType.id, typeForm);
      notify(t('Benefit type updated!'), 'success');
      setShowTypeModal(false);
      setEditingType(null);
      resetTypeForm();
      loadBenefitTypes();
    } catch (error) {
      notify(error.response?.data?.message || t('Error updating benefit type'), 'error');
    }
  };

  const handleDeleteType = async (id) => {
    if (!await confirmDialog(t('Delete this benefit type? This will affect all assignments.'))) return;
    
    try {
      await benefitsAPI.deleteType(id);
      notify(t('Benefit type deleted'), 'success');
      loadBenefitTypes();
    } catch (error) {
      notify(error.response?.data?.message || t('Error deleting benefit type'), 'error');
    }
  };

  const handleAssignBenefit = async (e) => {
    e.preventDefault();
    console.log('🔍 DATOS QUE SE ENVÍAN:');
    console.log('Employee ID:', assignForm.employee_id);
    console.log('Benefit Type ID:', assignForm.benefit_type_id);
    console.log('Amount:', assignForm.amount);
    console.log('Start Date:', assignForm.start_date);
    console.log('End Date:', assignForm.end_date);
    console.log('Full Form:', assignForm);




    try {
      await benefitsAPI.assignBenefit(assignForm);
      notify(t('Benefit assigned successfully!'), 'success');
      setShowAssignModal(false);
      resetAssignForm();
      
      // ✅ Limpiar filtros para ver el nuevo beneficio
      setFilters({ employee_id: '', status: 'active' });
      
      // Esperar un poco y recargar
      setTimeout(() => {
        loadEmployeeBenefits();
      }, 300);
    } catch (error) {
      notify(error.response?.data?.message || t('Error assigning benefit'), 'error');
    }
  };

  const handleUpdateBenefit = async (e) => {
    e.preventDefault();
    try {
      // Empty optional fields go as null: Postgres rejects '' for the
      // numeric percentage and the date end_date.
      await benefitsAPI.updateEmployeeBenefit(editingBenefit.id, {
        ...editBenefitForm,
        percentage: editBenefitForm.percentage === '' ? null : editBenefitForm.percentage,
        end_date: editBenefitForm.end_date || null
      });
      notify(t('Benefit updated!'), 'success');
      setShowEditModal(false);
      setEditingBenefit(null);
      loadEmployeeBenefits();
    } catch (error) {
      notify(t('Error updating benefit'), 'error');
    }
  };

  const handleCancelBenefit = async (id) => {
    if (!await confirmDialog(t('Cancel this benefit?'))) return;
    
    try {
      await benefitsAPI.cancelEmployeeBenefit(id);
      notify(t('Benefit cancelled'), 'success');
      loadEmployeeBenefits();
    } catch (error) {
      notify(t('Error cancelling benefit'), 'error');
    }
  };

  const handleDeleteBenefit = async (id) => {
    if (!await confirmDialog(t('Delete this benefit assignment?'))) return;
    
    try {
      await benefitsAPI.deleteEmployeeBenefit(id);
      notify(t('Benefit deleted'), 'success');
      loadEmployeeBenefits();
    } catch (error) {
      notify(t('Error deleting benefit'), 'error');
    }
  };

  const openEditTypeModal = (type) => {
    setEditingType(type);
    setTypeForm({
      name: type.name,
      code: type.code,
      description: type.description || '',
      calculation_type: type.calculation_type,
      default_amount: type.default_amount,
      is_taxable: type.is_taxable,
      is_active: type.is_active
    });
    setShowTypeModal(true);
  };

  const openEditBenefitModal = (benefit) => {
    setEditingBenefit(benefit);
    setEditBenefitForm({
      amount: benefit.amount,
      percentage: benefit.percentage || '',
      end_date: benefit.end_date || '',
      status: benefit.status,
      notes: benefit.notes || ''
    });
    setShowEditModal(true);
  };

  const resetTypeForm = () => {
    setTypeForm({
      name: '',
      code: '',
      description: '',
      calculation_type: 'fixed',
      default_amount: '',
      is_taxable: false,
      is_active: true
    });
  };

  const resetAssignForm = () => {
    setAssignForm({
      employee_id: '',
      benefit_type_id: '',
      amount: '',
      percentage: '',
      start_date: toLocalISODate(new Date()),
      end_date: '',
      notes: ''
    });
  };

  const canManage = ['super_admin', 'hr_manager', 'finance_manager'].includes(user?.role);

  return (
    <div className="benefits-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Benefits Management')}</h1>
          <p className="page-sub">{t('Manage employee benefits and allowances')}</p>
        </div>
        {canManage && (
          <div className="page-actions">
            {activeTab === 'types' && (
              <button onClick={() => setShowTypeModal(true)} className="btn btn--primary">
                <Plus aria-hidden="true" /> {t('New Benefit Type')}
              </button>
            )}
            {activeTab === 'assignments' && (
              <button onClick={() => setShowAssignModal(true)} className="btn btn--primary">
                <Plus aria-hidden="true" /> {t('Assign Benefit')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tabbar">
        <button
          className={`tabbar__tab${activeTab === 'types' ? ' is-active' : ''}`}
          onClick={() => setActiveTab('types')}
        >
          {t('Benefit Types')}
        </button>
        <button
          className={`tabbar__tab${activeTab === 'assignments' ? ' is-active' : ''}`}
          onClick={() => setActiveTab('assignments')}
        >
          {t('Employee Benefits')}
        </button>
      </div>

      {/* BENEFIT TYPES TAB */}
      {activeTab === 'types' && (
        <div className="ben-content">
          {loading ? (
            <div className="empty">{t('Loading benefit types...')}</div>
          ) : (
            <div className="ben-grid">
              {benefitTypes.map(type => (
                <div key={type.id} className="ben-card">
                  <div className="ben-card__head">
                    <h3>{type.name}</h3>
                    <StatusBadge variant="neutral">
                      {t(labelOf(CALCULATION_TYPE_LABELS, type.calculation_type))}
                    </StatusBadge>
                  </div>
                  
                  <p className="ben-card__code">{t('Code')}: {type.code}</p>
                  {type.description && (
                    <p className="ben-card__description">{type.description}</p>
                  )}
                  
                  <div className="ben-card__details">
                    <div className="ben-card__detail">
                      <span className="ben-card__label">{t('Default Amount')}:</span>
                      <span className="ben-card__value">{amountText(type.default_amount)}{type.calculation_type === 'percentage' ? '%' : ''}</span>
                    </div>
                    <div className="ben-card__detail">
                      <span className="ben-card__label">{t('Taxable')}:</span>
                      <span className="ben-card__value">{type.is_taxable ? t('Yes') : t('No')}</span>
                    </div>
                    <div className="ben-card__detail">
                      <span className="ben-card__label">{t('Status')}:</span>
                      <StatusBadge status={type.is_active ? 'active' : 'inactive'}>
                        {type.is_active ? t('Active') : t('Inactive')}
                      </StatusBadge>
                    </div>
                  </div>

                  {canManage && (
                    <div className="ben-card__actions">
                      <button
                        onClick={() => openEditTypeModal(type)}
                        className="btn btn--secondary"
                      >
                        <Pencil aria-hidden="true" /> {t('Edit')}
                      </button>
                      <button
                        onClick={() => handleDeleteType(type.id)}
                        className="btn btn--secondary ben-delete"
                      >
                        <Trash2 aria-hidden="true" /> {t('Delete')}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* EMPLOYEE BENEFITS TAB */}
      {activeTab === 'assignments' && (
        <div className="ben-content">
          <div className="card ben-filters">
            <div className="ben-field">
              <label className="field-label">{t('Employee')}</label>
              <select className="select"
                value={filters.employee_id}
                onChange={(e) => {
                  setFilters({...filters, employee_id: e.target.value});
                  setTimeout(loadEmployeeBenefits, 100);
                }}
              >
                <option value="">{t('All Employees')}</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="ben-field">
              <label className="field-label">{t('Status')}</label>
              <select className="select"
                value={filters.status}
                onChange={(e) => {
                  setFilters({...filters, status: e.target.value});
                  setTimeout(loadEmployeeBenefits, 100);
                }}
              >
                <option value="">{t('All Status')}</option>
                <option value="active">{t('Active')}</option>
                <option value="expired">{t('Expired')}</option>
                <option value="cancelled">{t('Cancelled')}</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="empty">{t('Loading benefits...')}</div>
          ) : employeeBenefits.length === 0 ? (
            <div className="empty">
              <p>{t('No benefits assigned yet')}</p>
              {canManage && (
                <button onClick={() => setShowAssignModal(true)} className="btn btn--primary">
                  {t('Assign First Benefit')}
                </button>
              )}
            </div>
          ) : (
            <div className="ben-table-wrap">
              <table className="data-table ben-table">
                <thead>
                  <tr>
                    <th>{t('Employee')}</th>
                    <th>{t('Benefit')}</th>
                    <th>{t('Type')}</th>
                    <th className="num">{t('Amount')}</th>
                    <th>{t('Start Date')}</th>
                    <th>{t('End Date')}</th>
                    <th>{t('Status')}</th>
                    <th>{t('Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {employeeBenefits.map(benefit => (
                    <tr key={benefit.id}>
                      <td>
                        <strong>{benefit.first_name} {benefit.last_name}</strong>
                        <br />
                        <small className="field-hint">{benefit.position}</small>
                      </td>
                      <td><strong>{benefit.benefit_name}</strong></td>
                      <td>
                        <StatusBadge variant="neutral">
                          {t(labelOf(CALCULATION_TYPE_LABELS, benefit.calculation_type))}
                        </StatusBadge>
                      </td>
                      <td className="num">
                        <strong>{benefit.calculation_type === 'percentage'
                          ? `${amountText(benefit.percentage)}%`
                          : formatCurrency(benefit.amount, benefit.currency || 'SRD', locale)}</strong>
                      </td>
                      <td>{parseLocalDate(benefit.start_date)?.toLocaleDateString(locale)}</td>
                      <td>{benefit.end_date ? parseLocalDate(benefit.end_date)?.toLocaleDateString(locale) : '-'}</td>
                      <td>
                        <StatusBadge status={benefit.status}>
                          {t(benefit.status)}
                        </StatusBadge>
                      </td>
                      <td>
                        <div className="ben-actions">
                          {canManage && (
                            <>
                              <button
                                onClick={() => openEditBenefitModal(benefit)}
                                type="button"
                                className="btn btn--secondary btn--icon"
                                title={t('Edit')}
                                aria-label={t('Edit')}
                              >
                                <Pencil aria-hidden="true" />
                              </button>
                              {benefit.status === 'active' && (
                                <button
                                  onClick={() => handleCancelBenefit(benefit.id)}
                                  type="button"
                                  className="btn btn--secondary btn--icon btn--danger-text"
                                  title={t('Cancel')}
                                  aria-label={t('Cancel')}
                                >
                                  <X aria-hidden="true" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteBenefit(benefit.id)}
                                type="button"
                                className="btn btn--secondary btn--icon btn--danger-text"
                                title={t('Delete')}
                                aria-label={t('Delete')}
                              >
                                <Trash2 aria-hidden="true" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* CREATE/EDIT BENEFIT TYPE MODAL */}
      {showTypeModal && (
        <Modal open title={editingType ? t('Edit Benefit Type') : t('Create Benefit Type')} onClose={() => { setShowTypeModal(false); setEditingType(null); resetTypeForm(); }} size="md">

            <form onSubmit={editingType ? handleUpdateType : handleCreateType} className="ben-dialog">
              <div className="ben-field">
                <label className="field-label">{t('Name')} *</label>
                <input className="input"
                  type="text"
                  value={typeForm.name}
                  onChange={(e) => setTypeForm({...typeForm, name: e.target.value})}
                  required
                  placeholder={t('e.g., Health Insurance')}
                />
              </div>

              <div className="ben-field">
                <label className="field-label">{t('Code')} *</label>
                <input className="input"
                  type="text"
                  value={typeForm.code}
                  onChange={(e) => setTypeForm({...typeForm, code: e.target.value.toUpperCase()})}
                  required
                  placeholder={t('e.g., HEALTH_INS')}
                  disabled={!!editingType}
                />
                <small className="field-hint">{t('Unique identifier (uppercase, no spaces)')}</small>
              </div>

              <div className="ben-field">
                <label className="field-label">{t('Description')}</label>
                <textarea className="textarea"
                  value={typeForm.description}
                  onChange={(e) => setTypeForm({...typeForm, description: e.target.value})}
                  rows="3"
                  placeholder={t('Brief description of the benefit...')}
                />
              </div>

              <div className="ben-form-row">
                <div className="ben-field">
                  <label className="field-label">{t('Calculation Type')} *</label>
                  <select className="select"
                    value={typeForm.calculation_type}
                    onChange={(e) => setTypeForm({...typeForm, calculation_type: e.target.value})}
                    required
                  >
                    <option value="fixed">{t('Fixed Amount')}</option>
                    <option value="percentage">{t('Percentage')}</option>
                  </select>
                </div>

                <div className="ben-field">
                  <label className="field-label">{typeForm.calculation_type === 'percentage' ? t('Default Percentage (%)') : t('Default Amount')} *</label>
                  <input className="input"
                    type="number"
                    value={typeForm.default_amount}
                    onChange={(e) => setTypeForm({...typeForm, default_amount: e.target.value})}
                    step="0.01"
                    min="0"
                    required
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="ben-field">
                <label className="ben-check">
                  <input
                    type="checkbox"
                    checked={typeForm.is_taxable}
                    onChange={(e) => setTypeForm({...typeForm, is_taxable: e.target.checked})}
                  />
                  <span>{t('This benefit is taxable')}</span>
                </label>
              </div>

              <div className="ben-field">
                <label className="ben-check">
                  <input
                    type="checkbox"
                    checked={typeForm.is_active}
                    onChange={(e) => setTypeForm({...typeForm, is_active: e.target.checked})}
                  />
                  <span>{t('Active (can be assigned to employees)')}</span>
                </label>
              </div>

              <div className="ben-form-actions">
                <button type="button" onClick={() => {
                  setShowTypeModal(false);
                  setEditingType(null);
                  resetTypeForm();
                }} className="btn btn--secondary">
                  {t('Cancel')}
                </button>
                <button type="submit" className="btn btn--primary">
                  {editingType ? t('Update') : t('Create')}
                </button>
              </div>
            </form>
        </Modal>
      )}

      {/* ASSIGN BENEFIT MODAL */}
      {showAssignModal && (
        <Modal open title={t('Assign Benefit to Employee')} onClose={() => { setShowAssignModal(false); resetAssignForm(); }} size="md">

            <form onSubmit={handleAssignBenefit} className="ben-dialog">
              <div className="ben-field">
                <label className="field-label">{t('Employee')} *</label>
                <select className="select"
                  value={assignForm.employee_id}
                  onChange={(e) => setAssignForm({...assignForm, employee_id: e.target.value})}
                  required
                >
                  <option value="">{t('Select employee')}</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} - {emp.position}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ben-field">
                <label className="field-label">{t('Benefit Type')} *</label>
                <select className="select"
                  value={assignForm.benefit_type_id}
                  onChange={(e) => {
                    const selectedType = benefitTypes.find(t => t.id === parseInt(e.target.value));
                    // A percentage type's default is a percentage: it goes in
                    // percentage (amount 0). It used to go in amount, which
                    // payroll paid as money.
                    const isPercentage = selectedType?.calculation_type === 'percentage';
                    setAssignForm({
                      ...assignForm,
                      benefit_type_id: e.target.value,
                      amount: isPercentage ? '0' : (selectedType?.default_amount || ''),
                      percentage: isPercentage ? (selectedType?.default_amount || '') : ''
                    });
                  }}
                  required
                >
                  <option value="">{t('Select benefit type')}</option>
                  {benefitTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name} ({t(labelOf(CALCULATION_TYPE_LABELS, type.calculation_type))})
                    </option>
                  ))}
                </select>
              </div>

              <div className="ben-form-row">
                {/* The field the type uses: payroll pays a fixed type's amount
                    and a percentage type's percentage. */}
                {benefitTypes.find(t => t.id === parseInt(assignForm.benefit_type_id))?.calculation_type === 'percentage' ? (
                  <div className="ben-field">
                    <label className="field-label">{t('Percentage (%)')} *</label>
                    <input className="input"
                      type="number"
                      value={assignForm.percentage}
                      onChange={(e) => setAssignForm({...assignForm, percentage: e.target.value})}
                      step="0.01"
                      min="0.01"
                      max="100"
                      required
                      placeholder="0.00"
                    />
                  </div>
                ) : (
                  <div className="ben-field">
                    <label className="field-label">{t('Amount')} *</label>
                    <input className="input"
                      type="number"
                      value={assignForm.amount}
                      onChange={(e) => setAssignForm({...assignForm, amount: e.target.value})}
                      step="0.01"
                      min="0"
                      required
                      placeholder="0.00"
                    />
                  </div>
                )}
              </div>

              <div className="ben-form-row">
                <div className="ben-field">
                  <label className="field-label">{t('Start Date')} *</label>
                  <input className="input"
                    type="date"
                    value={assignForm.start_date}
                    onChange={(e) => setAssignForm({...assignForm, start_date: e.target.value})}
                    required
                  />
                </div>

                <div className="ben-field">
                  <label className="field-label">{t('End Date (Optional)')}</label>
                  <input className="input"
                    type="date"
                    value={assignForm.end_date}
                    onChange={(e) => setAssignForm({...assignForm, end_date: e.target.value})}
                    min={assignForm.start_date}
                  />
                </div>
              </div>

              <div className="ben-field">
                <label className="field-label">{t('Notes')}</label>
                <textarea className="textarea"
                  value={assignForm.notes}
                  onChange={(e) => setAssignForm({...assignForm, notes: e.target.value})}
                  rows="3"
                  placeholder={t('Optional notes...')}
                />
              </div>

              <div className="ben-form-actions">
                <button type="button" onClick={() => {
                  setShowAssignModal(false);
                  resetAssignForm();
                }} className="btn btn--secondary">
                  {t('Cancel')}
                </button>
                <button type="submit" className="btn btn--primary">
                  {t('Assign Benefit')}
                </button>
              </div>
            </form>
        </Modal>
      )}

      {/* EDIT BENEFIT ASSIGNMENT MODAL */}
      {showEditModal && editingBenefit && (
        <Modal open title={t('Edit Benefit Assignment')} onClose={() => { setShowEditModal(false); setEditingBenefit(null); }} size="md">

            <form onSubmit={handleUpdateBenefit} className="ben-dialog">
              <div className="ben-info">
                <p><strong>{t('Employee')}:</strong> {editingBenefit.first_name} {editingBenefit.last_name}</p>
                <p><strong>{t('Benefit')}:</strong> {editingBenefit.benefit_name}</p>
                <p><strong>{t('Start Date')}:</strong> {parseLocalDate(editingBenefit.start_date)?.toLocaleDateString(locale)}</p>
              </div>

              <div className="ben-form-row">
                <div className="ben-field">
                  <label className="field-label">{t('Amount')} *</label>
                  <input className="input"
                    type="number"
                    value={editBenefitForm.amount}
                    onChange={(e) => setEditBenefitForm({...editBenefitForm, amount: e.target.value})}
                    step="0.01"
                    min="0"
                    required
                  />
                </div>

                <div className="ben-field">
                  <label className="field-label">{t('Percentage')}</label>
                  <input className="input"
                    type="number"
                    value={editBenefitForm.percentage}
                    onChange={(e) => setEditBenefitForm({...editBenefitForm, percentage: e.target.value})}
                    step="0.01"
                    min="0"
                    max="100"
                  />
                </div>
              </div>

              <div className="ben-field">
                <label className="field-label">{t('End Date')}</label>
                <input className="input"
                  type="date"
                  value={editBenefitForm.end_date?.split('T')[0] || ''}
                  onChange={(e) => setEditBenefitForm({...editBenefitForm, end_date: e.target.value})}
                />
              </div>

              <div className="ben-field">
                <label className="field-label">{t('Status')} *</label>
                <select className="select"
                  value={editBenefitForm.status}
                  onChange={(e) => setEditBenefitForm({...editBenefitForm, status: e.target.value})}
                  required
                >
                  <option value="active">{t('Active')}</option>
                  <option value="expired">{t('Expired')}</option>
                  <option value="cancelled">{t('Cancelled')}</option>
                </select>
              </div>

              <div className="ben-field">
                <label className="field-label">{t('Notes')}</label>
                <textarea className="textarea"
                  value={editBenefitForm.notes}
                  onChange={(e) => setEditBenefitForm({...editBenefitForm, notes: e.target.value})}
                  rows="3"
                />
              </div>

              <div className="ben-form-actions">
                <button type="button" onClick={() => {
                  setShowEditModal(false);
                  setEditingBenefit(null);
                }} className="btn btn--secondary">
                  {t('Cancel')}
                </button>
                <button type="submit" className="btn btn--primary">
                  {t('Update Benefit')}
                </button>
              </div>
            </form>
        </Modal>
      )}
    </div>
  );
};

export default Benefits;
